"use client";
import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Menu, Briefcase, Home, Phone, Mail, MapPin, House, Wallet } from 'lucide-react';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
        setActiveSubDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleMouseEnterDropdown = (linkName) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(linkName);
  };

  const handleMouseLeaveDropdown = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300);
  };

  // Helper function to create loan hash
  const createLoanHash = (loanName) => {
    return `#calculator-loan=${encodeURIComponent(loanName)}`;
  };

  const businessLoanSubItems = {
    'Overview': [
      { name: 'Business Loan', path: createLoanHash('Business Loan') },
      { name: 'Business Loan Interest Rates', path: createLoanHash('Business Loan Interest Rates') },
      { name: 'Business Loan low CIBIL Score', path: createLoanHash('Business Loan low CIBIL Score') }
    ],
    'By Schemes': [
      { name: 'Dairy Farming Loan', path: createLoanHash('Dairy Farming Loan') },
      { name: 'Small Business Loan', path: createLoanHash('Small Business Loan') },
      { name: 'Goat Farming Loan', path: createLoanHash('Goat Farming Loan') },
      { name: 'Startups Loan', path: createLoanHash('Startups Loan') },
      { name: 'Poultry Farm Loan', path: createLoanHash('Poultry Farm Loan') },
      { name: 'Professional Loan', path: createLoanHash('Professional Loan') }
    ],
    'By Need': [
      { name: 'Mudra Loan', path: createLoanHash('Mudra Loan') },
      { name: 'PMEGP Loan', path: createLoanHash('PMEGP Loan') },
      { name: 'Letter of Credit', path: createLoanHash('Letter of Credit') },
      { name: 'CGTMSE Loan', path: createLoanHash('CGTMSE Loan') },
      { name: 'Overdraft Loan', path: createLoanHash('Overdraft Loan') },
      { name: 'Working Capital Loan', path: createLoanHash('Working Capital Loan') }
    ],
    'By Profession': [
      { name: 'Loan for CA', path: createLoanHash('Loan for CA') },
      { name: 'Loan for Doctors', path: createLoanHash('Loan for Doctors') }
    ]
  };

  const homeLoanSubItems = {
    'Overview': [
      { name: 'Home Loan', path: createLoanHash('Home Loan') },
      { name: 'Home Loan Interest Rates', path: createLoanHash('Home Loan Interest Rates') },
      { name: 'Home Loan Balance Transfer', path: createLoanHash('Home Loan Balance Transfer') },
      { name: 'Home Loan Low CIBIL Score', path: createLoanHash('Home Loan Low CIBIL Score') }
    ],
    'By Amount': [
      { name: '10 Lakh Home Loan', path: createLoanHash('10 Lakh Home Loan') },
      { name: '15 Lakh Home Loan', path: createLoanHash('15 Lakh Home Loan') },
      { name: '20 Lakh Home Loan', path: createLoanHash('20 Lakh Home Loan') },
      { name: '30 Lakh Home Loan', path: createLoanHash('30 Lakh Home Loan') },
      { name: '40 Lakh Home Loan', path: createLoanHash('40 Lakh Home Loan') },
      { name: '60 Lakh Home Loan', path: createLoanHash('60 Lakh Home Loan') }
    ],
    'By Schemes': [
      { name: 'Home Renovation Loan', path: createLoanHash('Home Renovation Loan') },
      { name: 'Plot Loan', path: createLoanHash('Plot Loan') },
      { name: 'Top up Home Loan', path: createLoanHash('Top up Home Loan') },
      { name: 'Home Construction Loan', path: createLoanHash('Home Construction Loan') },
      { name: 'NRI Home Loan', path: createLoanHash('NRI Home Loan') },
      { name: 'Home Extension Loan', path: createLoanHash('Home Extension Loan') }
    ],
    'By Profession': [
      { name: 'Home Loan for Self Employed', path: createLoanHash('Home Loan for Self Employed') },
      { name: 'Home Loan for Women', path: createLoanHash('Home Loan for Women') }
    ]
  };

  const otherLoansSubItems = {
    'Loans': [
      { name: 'Loan Against Property', path: createLoanHash('Loan Against Property') },
      { name: 'Loan Against Car', path: createLoanHash('Loan Against Car') },
      { name: 'Micro Loan', path: createLoanHash('Micro Loan') },
      { name: 'Two Wheeler Loan', path: createLoanHash('Two Wheeler Loan') },
      { name: 'Gold Loan', path: createLoanHash('Gold Loan') },
      { name: 'Education Loan', path: createLoanHash('Education Loan') },
      { name: 'Car Loan', path: createLoanHash('Car Loan') }
    ]
  };

  const loanTypes = [
    {
      name: 'Business Loan',
      icon: <Briefcase className="w-6 h-6 md:w-7 md:h-7" />,
      subItems: businessLoanSubItems
    },
    {
      name: 'Home Loan',
      icon: <Home className="w-6 h-6 md:w-7 md:h-7" />,
      subItems: homeLoanSubItems
    },
    {
      name: 'Loans',
      icon: <Wallet className="w-6 h-6 md:w-7 md:h-7" />,
      subItems: otherLoansSubItems
    }
  ];

  const navLinks = [
    {
      name: 'Home',
      path: '/',
      icon: <House className="w-4 h-4" />
    },
    {
      name: 'Loans',
      hasDropdown: true
    },
    {
      name: 'Calculator',
      path: '/#calculator'
    },
    {
      name: 'Apply',
      path: '/#calculator',
      highlight: true
    },
    {
      name: 'Contact',
      path: '/#contact'
    }
  ];

  const toggleDropdown = (linkName) => {
    setActiveDropdown(activeDropdown === linkName ? null : linkName);
    setActiveSubDropdown(null);
  };

  const toggleSubDropdown = (itemName) => {
    setActiveSubDropdown(activeSubDropdown === itemName ? null : itemName);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setActiveSubDropdown(null);
  };

  const handleLoanClick = () => {
    setActiveDropdown(null);
    closeMobileMenu();
  };

  return (
    <nav
      ref={dropdownRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 shadow-lg'
        : 'bg-gradient-to-r from-blue-800 via-blue-700 to-blue-800'
        }`}
    >
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex-shrink-0 group">
            <a
              href="/"
              className="flex items-center transition-transform duration-300 hover:scale-105"
            >
              <img
                src="/dhanpluslogo.png"
                alt="DhanPlus Logo"
                className="h-10 sm:h-12 md:h-14 w-auto object-contain"
              />
            </a>
          </div>

          <div className="hidden lg:flex lg:items-center lg:space-x-1">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => link.hasDropdown && handleMouseEnterDropdown(link.name)}
                onMouseLeave={() => link.hasDropdown && handleMouseLeaveDropdown()}
              >
                {link.hasDropdown ? (
                  <>
                    <button
                      className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-white font-medium transition-all duration-200 ${activeDropdown === link.name
                        ? 'bg-blue-700 shadow-md'
                        : 'hover:bg-blue-700/50'
                        }`}
                    >
                      <span>{link.name}</span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180' : ''
                          }`}
                      />
                    </button>

                    {/* Full-Width Full-Height Mega Menu */}
                    <div
                      className={`fixed left-0 right-0 top-14 sm:top-16 bottom-0 bg-white shadow-2xl transform transition-all duration-300 ${activeDropdown === link.name
                        ? 'opacity-100 translate-y-0 visible'
                        : 'opacity-0 -translate-y-4 invisible pointer-events-none'
                        }`}
                    >
                      <div className="w-full h-full px-4 sm:px-6 lg:px-8 py-6 lg:py-10 overflow-y-auto">
                        <div className="max-w-7xl mx-auto h-full">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8 h-full">
                            {loanTypes.map((loanType) => (
                              <div
                                key={loanType.name}
                                className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 lg:p-6 border border-blue-100 hover:border-blue-300 hover:shadow-xl transition-all duration-300 flex flex-col h-full"
                              >
                                {/* Loan Type Header */}
                                <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-blue-200 flex-shrink-0">
                                  <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center text-white shadow-lg">
                                    {loanType.icon}
                                  </div>
                                  <h3 className="text-lg lg:text-xl font-bold text-blue-900">
                                    {loanType.name}
                                  </h3>
                                </div>

                                {/* Loan Categories - Scrollable if needed */}
                                <div className="flex-1 overflow-y-auto">
                                  <div className="grid grid-cols-1 gap-3 lg:gap-4">
                                    {Object.entries(loanType.subItems).map(([category, items]) => (
                                      <div key={category}>
                                        <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2 px-1">
                                          {category}
                                        </h4>
                                        <div className="space-y-0.5">
                                          {items.map((subItem) => (
                                            <a
                                              key={subItem.name}
                                              href={subItem.path}
                                              onClick={handleLoanClick}
                                              className="block px-2 py-1.5 text-xs lg:text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-900 rounded-md transition-all duration-200 font-medium"
                                            >
                                              {subItem.name}
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <a
                    href={link.path}
                    className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg font-medium transition-all duration-200 ${link.highlight
                      ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                      : 'text-white hover:bg-blue-700/50'
                      }`}
                  >
                    {link.icon && <span>{link.icon}</span>}
                    <span>{link.name}</span>
                  </a>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-white hover:bg-blue-700/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all duration-200"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5 sm:w-6 sm:h-6 animate-spin-in" />
            ) : (
              <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-blue-900 border-t border-blue-700 overflow-hidden transition-all duration-300 ${isMobileMenuOpen
          ? 'max-h-screen opacity-100'
          : 'max-h-0 opacity-0'
          }`}
      >
        <div className="px-3 py-3 space-y-2 max-h-[80vh] overflow-y-auto">
          {navLinks.map((link) => (
            <div key={link.name}>
              {link.hasDropdown ? (
                <div className="space-y-1">
                  <button
                    onClick={() => toggleDropdown(link.name)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-white font-medium transition-all duration-200 ${activeDropdown === link.name
                      ? 'bg-blue-800 shadow-md'
                      : 'hover:bg-blue-800/50'
                      }`}
                  >
                    <span className="text-sm sm:text-base">{link.name}</span>
                    <ChevronDown
                      className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180' : ''
                        }`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${activeDropdown === link.name
                      ? 'max-h-[1000px] opacity-100'
                      : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="pl-2 pt-1 space-y-1.5">
                      {loanTypes.map((loanType) => (
                        <div key={loanType.name}>
                          <div className="space-y-1">
                            <button
                              onClick={() => toggleSubDropdown(loanType.name)}
                              className="w-full flex items-center justify-between p-2.5 rounded-lg bg-blue-800/30 hover:bg-blue-700 transition-all duration-200 text-white"
                            >
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-blue-700 rounded-lg flex items-center justify-center text-white">
                                  {loanType.icon}
                                </div>
                                <span className="text-xs sm:text-sm font-semibold">{loanType.name}</span>
                              </div>
                              <ChevronDown
                                className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-200 ${activeSubDropdown === loanType.name ? 'rotate-180' : ''
                                  }`}
                              />
                            </button>

                            <div
                              className={`overflow-hidden transition-all duration-300 ${activeSubDropdown === loanType.name
                                ? 'max-h-[800px] opacity-100'
                                : 'max-h-0 opacity-0'
                                }`}
                            >
                              <div className="pl-2 pt-1 space-y-1.5">
                                {Object.entries(loanType.subItems).map(([category, items]) => (
                                  <div key={category} className="mb-1.5">
                                    <p className="text-[10px] sm:text-xs font-semibold text-blue-300 uppercase tracking-wide px-2 py-1">
                                      {category}
                                    </p>
                                    {items.map((subItem) => (
                                      <a
                                        key={subItem.name}
                                        href={subItem.path}
                                        onClick={closeMobileMenu}
                                        className="block px-2.5 py-1.5 text-xs sm:text-sm text-blue-100 hover:bg-blue-700 hover:text-white rounded-md transition-colors duration-200"
                                      >
                                        {subItem.name}
                                      </a>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a
                  href={link.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${link.highlight
                    ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300 shadow-md'
                    : 'text-white hover:bg-blue-800/50'
                    }`}
                >
                  {link.icon && <span>{link.icon}</span>}
                  <span>{link.name}</span>
                </a>
              )}
            </div>
          ))}

          <div className="mt-4 pt-3 border-t border-blue-700 space-y-2">
            <div className="flex items-center space-x-2 text-blue-200 text-xs sm:text-sm">
              <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>1800-123-4567</span>
            </div>
            <div className="flex items-center space-x-2 text-blue-200 text-xs sm:text-sm">
              <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>support@redloan.com</span>
            </div>
            <div className="flex items-center space-x-2 text-blue-200 text-xs sm:text-sm">
              <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Mumbai, India</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin-in {
          from { transform: rotate(0deg); }
          to { transform: rotate(90deg); }
        }
        .animate-spin-in {
          animation: spin-in 0.3s ease-out;
        }
      `}</style>
    </nav>
  );
}
